import React from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { NavLink } from "react-router-dom";

import { PageLayout } from "../../common/page-layout/page-layout";
import { Loading } from "../../common/loading/loading";
import { useUiFeedback } from "../../../context/ui-feedback/ui-feedback";
import {
  createWaiting,
  closeWaiting,
  createErrorFeedback,
  createSuccessFeedback,
} from "../../../context/ui-feedback/action";
import { useAlarm } from "../../../context/alarm/alarm";
import * as ACTION from "../../../context/alarm/action";
import {
  useRunEmailJob,
  useRunServiceWorksheetJob,
  useRunOnSiteServiceWorksheetJob,
} from "../../../api/alarm-api";

export const Alarms = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const { uiFeedbackDispatch } = useUiFeedback();

  const runEmailJob = useRunEmailJob();
  const runServiceWorksheetJob = useRunServiceWorksheetJob();
  const runOnSiteServiceWorksheetJob = useRunOnSiteServiceWorksheetJob();

  const {
    dispatch,
    state: { task },
  } = useAlarm();

  const handleRefresh = () => {
    ACTION.updateAlarm(dispatch);
  };

  const handleOnSiteServiceWorksheet = async () => {
    createWaiting(uiFeedbackDispatch);
    try {
      await runOnSiteServiceWorksheetJob((data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          generateFeedback("onsiteservicewworksheet", data.status);
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        generateFeedback("onsiteservicewworksheet", "ERROR");
      }
    } finally {
      ACTION.updateAlarm(dispatch);
      closeWaiting(uiFeedbackDispatch);
    }
  };

  const handleServicewWorksheet = async () => {
    createWaiting(uiFeedbackDispatch);
    try {
      await runServiceWorksheetJob((data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          generateFeedback("servicewworksheet", data.status);
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        generateFeedback("servicewworksheet", "ERROR");
      }
    } finally {
      ACTION.updateAlarm(dispatch);
      closeWaiting(uiFeedbackDispatch);
    }
  };

  const handleEmail = async () => {
    createWaiting(uiFeedbackDispatch);
    try {
      await runEmailJob((data) => {
        if (data instanceof Error) {
          throw data;
        } else {
          generateFeedback("email", data.status);
        }
      });
    } catch (ex) {
      if (ex.response?.status === 401 || ex.response?.status === 403) {
        navigate("/");
      } else {
        generateFeedback("email", "ERROR");
      }
    } finally {
      ACTION.updateAlarm(dispatch);
      closeWaiting(uiFeedbackDispatch);
    }
  };

  const generateFeedback = (key, status) => {
    switch (status) {
      case "SUCCESS":
        const successMsg = intl.formatMessage({
          id: `page.alarms.success.${key}.run`,
        });
        createSuccessFeedback(uiFeedbackDispatch, successMsg);
        break;
      case "NOT_ACTIVE":
        const errorTitleNotActive = intl.formatMessage({
          id: `page.alarms.exception.${key}.notactive.title`,
        });
        const errorMsgNotActive = intl.formatMessage({
          id: `page.alarms.exception.${key}.notactive.description`,
        });
        createErrorFeedback(
          uiFeedbackDispatch,
          errorTitleNotActive,
          errorMsgNotActive,
        );
        break;
      case "NOT_OK":
        const errorTitleNotOk = intl.formatMessage({
          id: `page.alarms.exception.${key}.notok.title`,
        });
        const errorMsgNotOk = intl.formatMessage({
          id: `page.alarms.exception.${key}.notok.description`,
        });
        createErrorFeedback(uiFeedbackDispatch, errorTitleNotOk, errorMsgNotOk);
        break;
      default:
        const errorTitle = intl.formatMessage({
          id: `page.alarms.exception.${key}.run.title`,
        });
        const errorMs = intl.formatMessage({
          id: `page.alarms.exception.${key}.run.description`,
        });
        createErrorFeedback(uiFeedbackDispatch, errorTitle, errorMs);
        break;
    }
  };

  const buildPaths = () => {
    return [
      {
        link: "/",
        title: intl.formatMessage({ id: "page.welcome.title" }),
      },
    ];
  };

  return (
    <>
      <PageLayout
        paths={buildPaths()}
        title={intl.formatMessage({ id: "page.alarms.title" })}
      >
        <>
          {task === null ? (
            <Loading />
          ) : (
            <>
              <div>
                <Button
                  variant={`outline-primary`}
                  title={intl.formatMessage({
                    id: "data.alarm.button.refresh",
                  })}
                  onClick={handleRefresh}
                >
                  <FormattedMessage id="data.alarm.button.refresh" />
                </Button>
              </div>
              <br />
              <table>
                <tbody>
                  <tr>
                    <td>
                      <FormattedMessage id="data.alarm.label.onSiteServiceWorksheet.label" />
                    </td>
                    <td>
                      <Button
                        className="ms-2 mb-2"
                        variant={`outline-primary`}
                        title={intl.formatMessage(
                          {
                            id: "data.alarm.label.onSiteServiceWorksheet.button",
                          },
                          { count: task.onSiteServiceWorksheet },
                        )}
                        onClick={handleOnSiteServiceWorksheet}
                        disabled={task.onSiteServiceWorksheet === 0}
                      >
                        <FormattedMessage
                          id="data.alarm.label.onSiteServiceWorksheet.button"
                          values={{ count: task.onSiteServiceWorksheet }}
                        />
                      </Button>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <FormattedMessage id="data.alarm.label.notApproveonSiteServiceWorksheetTask.label" />
                    </td>
                    <td>
                      <NavLink
                        className="ms-2 mb-2 btn btn-outline-primary"
                        title={intl.formatMessage(
                          {
                            id: "data.alarm.label.notApproveonSiteServiceWorksheetTask.button",
                          },
                          { count: task.notApproveonSiteServiceWorksheetTask },
                        )}
                        to="/onsiteserviceworksheets"
                      >
                        <FormattedMessage
                          id="data.alarm.label.notApproveonSiteServiceWorksheetTask.button"
                          values={{
                            count: task.notApproveonSiteServiceWorksheetTask,
                          }}
                        />
                      </NavLink>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <FormattedMessage id="data.alarm.label.servicewWorksheet.label" />
                    </td>
                    <td>
                      <Button
                        className="ms-2 mb-2"
                        variant={`outline-primary`}
                        title={intl.formatMessage(
                          {
                            id: "data.alarm.label.servicewWorksheet.button",
                          },
                          { count: task.servicewWorksheet },
                        )}
                        onClick={handleServicewWorksheet}
                        disabled={task.servicewWorksheet === 0}
                      >
                        <FormattedMessage
                          id="data.alarm.label.servicewWorksheet.button"
                          values={{ count: task.servicewWorksheet }}
                        />
                      </Button>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <FormattedMessage id="data.alarm.label.email.label" />
                    </td>
                    <td>
                      <Button
                        className="ms-2 mb-2"
                        variant={`outline-primary`}
                        title={intl.formatMessage(
                          {
                            id: "data.alarm.label.email.button",
                          },
                          { count: task.email },
                        )}
                        onClick={handleEmail}
                        disabled={task.email === 0}
                      >
                        <FormattedMessage
                          id="data.alarm.label.email.button"
                          values={{ count: task.email }}
                        />
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </>
      </PageLayout>
    </>
  );
};
